import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Backdrop,
  ChartsSetWrapperNew,
  FormFieldSet,
  InfoCardNewAdv,
  InfoWrapper,
  LogoLoader,
  Modal,
  TableAdv,
} from "../../../components";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminBDMMetricsGroupStart,
  getAdminBDMMetricsOverviewStart,
  getAdminCompareMetricsStart,
  getAdminMetricsStart,
  getAdminRAMetricsGroupStart,
  getAdminRAMetricsOverviewStart,
  getAdminRecruiterMetricsGroupStart,
  getAdminRecruiterMetricsOverviewStart,
  getAdminUsersStart,
} from "../../../redux/slices/atsDashboard/atsDashboardSlice";
import { asArray, getEndOfDay, getStartOfDay } from "../../../utils/common";
import { userRefCount } from "../../../redux/slices/bdmjobs/bdmjobsApi";
import {
  combineByUserTimezone,
  getZoneBasedOnLocaleInfo,
} from "../../../utils/dateUtils";
import dayjs from "dayjs";
import { formatWords2 } from "../../../utils/stringUtils";
import KpiDashboardViewModal from "./Modals/KpiDashboardViewModal";
import { mapTableRows } from "../../../utils";
import { usePagination } from "../../../hooks";

const RA_METRIC_CONFIG = [
  { key: "accounts", label: "Accounts" },
  { key: "contacts", label: "Contacts" },
  { key: "jobOpenings", label: "Job Openings" },
  { key: "confirm", label: "Confirm" },
];

const BDM_METRIC_CONFIG = [
  { key: "positives", label: "Positives" },
  { key: "agreementDone", label: "Agreement Done" },
  { key: "internalSubmission", label: "Internal Submission" },
  { key: "clientSubmission", label: "Client Submission" },
  { key: "interviewScheduled", label: "Interview Scheduled" },
  { key: "joined", label: "Joined" },
  { key: "backout", label: "Backout" },
];

const RECRUITER_METRIC_CONFIG = [
  { key: "activeJobs", label: "Active Jobs" },
  { key: "internalSubmissions", label: "Internal Submission" },
  { key: "clientSubmissions", label: "Client Submission" },
  { key: "interviewScheduled", label: "Interview Scheduled" },
  { key: "closedJobs", label: "Closed Jobs" },
  { key: "joined", label: "Joined" },
];

export function getDateList(from, to) {
  if (!from) return [];

  const start = dayjs(from).startOf("day");
  const end = to ? dayjs(to).startOf("day") : dayjs().startOf("day");

  if (!start.isValid() || !end.isValid()) return [];

  const dates = [];
  let current = start;

  while (current.isSameOrBefore(end)) {
    dates.push(current.format("YYYY-MM-DD"));
    current = current.add(1, "day");
  }

  return dates;
}

function getCompareDateRange(from, to) {
  if (!from || !to) return null;

  const start = new Date(from);
  const end = new Date(to);

  const dayDiff = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

  const compareTo = new Date(start);
  compareTo.setMilliseconds(-1);

  const compareFrom = new Date(compareTo);
  compareFrom.setDate(compareFrom.getDate() - (dayDiff - 1));

  compareFrom.setHours(0, 0, 0, 0);
  compareTo.setHours(23, 59, 59, 999);

  return { from: compareFrom, to: compareTo };
}

// function buildTeamMetricsData({
//   teamMetrics = [],
//   reportToHierarchy = [],
//   from,
//   to,
//   metricConfig = [],
// }) {
//   const userTimezone = getZoneBasedOnLocaleInfo();
//   if (!from || !to) return [];

//   // Generate date list
//   const dates = [];
//   let current = dayjs(from).startOf("day");
//   const end = dayjs(to).startOf("day");

//   while (current.isSameOrBefore(end)) {
//     dates.push(current.format("YYYY-MM-DD"));
//     current = current.add(1, "day");
//   }

//   // Organize metrics by date and user
//   const metricsByDate = {};

//   teamMetrics.forEach((item) => {
//     if (!item || !item.date) return;

//     const dateKey = dayjs.utc(item.date).tz(userTimezone).format("YYYY-MM-DD");
//     metricsByDate[dateKey] = item.users || {};
//   });

//   // Get all user IDs that have metrics
//   const teamMetricUserIds = new Set();
//   Object.values(metricsByDate).forEach((users) => {
//     if (users && typeof users === "object") {
//       Object.keys(users).forEach((uiid) => {
//         teamMetricUserIds.add(String(uiid));
//       });
//     }
//   });

//   // Filter users that have metrics
//   const filteredUsers = (reportToHierarchy || []).filter(
//     (user) => user && teamMetricUserIds.has(String(user.uiid))
//   );

//   const series = [];

//   // Build series data for each user and metric
//   filteredUsers.forEach((user) => {
//     const uiid = String(user.uiid);
//     const groupName =
//       `${user.firstName || ""} ${user.lastName || ""}`.trim() || `User ${uiid}`;

//     metricConfig.forEach(({ key, label }) => {
//       const data = dates.map((date) => {
//         const userMetrics = metricsByDate[date]?.[uiid];
//         const value = userMetrics?.[key];
//         const numValue =
//           value != null && isFinite(Number(value)) ? Number(value) : 0;
//         return numValue;
//       });

//       series.push({
//         name: label || key,
//         data,
//         group: groupName,
//       });
//     });
//   });

//   return series;
// }

function buildTeamMetricsData({
  teamMetrics = [],
  reportToHierarchy = [],
  from,
  to,
  metricConfig = [],
}) {
  const userTimezone = getZoneBasedOnLocaleInfo();
  if (!from || !to) return [];

  // Generate date list
  const dates = [];
  let current = dayjs(from).startOf("day");
  const end = dayjs(to).startOf("day");

  while (current.isSameOrBefore(end)) {
    dates.push(current.format("YYYY-MM-DD"));
    current = current.add(1, "day");
  }

  // Organize metrics by date and user
  const metricsByDate = {};

  teamMetrics.forEach((item) => {
    if (!item || !item.date) return;

    const dateKey = dayjs.utc(item.date).tz(userTimezone).format("YYYY-MM-DD");
    metricsByDate[dateKey] = item.users || {};
  });

  // Collect all user IDs that have metrics
  const teamMetricUserIds = new Set();
  Object.values(metricsByDate).forEach((users) => {
    if (users && typeof users === "object") {
      Object.keys(users).forEach((uiid) => {
        teamMetricUserIds.add(String(uiid));
      });
    }
  });

  // Filter only users with metrics
  const filteredUsers = (reportToHierarchy || []).filter(
    (user) => user && teamMetricUserIds.has(String(user.uiid)),
  );

  let seriesIdCounter = 1;
  const result = [];

  // Build grouped series per user
  filteredUsers.forEach((user) => {
    const uiid = String(user.uiid);
    const seriesName =
      `${formatWords2(user.firstName || "")} ${formatWords2(user.lastName || "")}`.trim() ||
      `User ${uiid}`;

    const seriesData = metricConfig.map(({ key, label }) => {
      const data = dates.map((date) => {
        const userMetrics = metricsByDate[date]?.[uiid];
        const value = userMetrics?.[key];
        return value != null && isFinite(Number(value)) ? Number(value) : 0;
      });

      return {
        name: label || key,
        data,
      };
    });

    result.push({
      seriesName,
      seriesId: seriesIdCounter++,
      seriesData,
    });
  });

  return result;
}

// Helper to normalize dates for comparison
const normalizeDateForComparison = (date) => {
  if (!date) return null;
  return dayjs(date).format("YYYY-MM-DD");
};

const AdminDashboard = ({ finalDataDuration }) => {
  const dispatch = useDispatch();
  const {
    loading,
    adminMetricsOverview,
    adminMetricsGroup,
    adminRAUsers,
    adminBDMUsers,
    adminRecruiterUsers,
    adminMetrics,
    adminCompareMetrics,
  } = useSelector((state) => state.atsDashboard);
  const { page, limit, setPage, handleLimitChange } = usePagination(10);

  const [selectedRaUsers, setSelectedRaUsers] = useState([]);
  const [selectedBdmUsers, setSelectedBdmUsers] = useState([]);
  const [selectedRecruiterUsers, setSelectedRecruiterUsers] = useState([]);
  //state for kpi modals
  const [selectedKpi, setSelectedKpi] = useState({ key: null, label: null });
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Individual date ranges for each pie chart
  const [raDateRange, setRaDateRange] = useState({ from: null, to: null });
  const [bdmDateRange, setBdmDateRange] = useState({ from: null, to: null });
  const [recruiterDateRange, setRecruiterDateRange] = useState({
    from: null,
    to: null,
  });

  // Column chart date ranges (SEPARATE from pie charts)
  const [raColumnDateRange, setRaColumnDateRange] = useState({
    from: null,
    to: null,
  });
  const [bdmColumnDateRange, setBdmColumnDateRange] = useState({
    from: null,
    to: null,
  });
  const [recruiterColumnDateRange, setRecruiterColumnDateRange] = useState({
    from: null,
    to: null,
  });

  // Use refs to track if we've initialized
  const initializedRef = useRef(false);
  const fetchingRef = useRef({
    raOverview: false,
    raGroup: false,
    bdmOverview: false,
    bdmGroup: false,
    recruiterOverview: false,
    recruiterGroup: false,
  });

  const compareDateRange = useMemo(() => {
    if (!finalDataDuration) return null;
    return getCompareDateRange(finalDataDuration.from, finalDataDuration.to);
  }, [finalDataDuration]);

  useEffect(() => {
    const raOptions = Array.isArray(adminRAUsers)
      ? adminRAUsers.map((user) => ({
          label:
            `${formatWords2(user.firstName ?? "")} ${formatWords2(user.lastName ?? "")}`.trim(),
          id: user.uiid,
        }))
      : [];
    const bdmOptions = Array.isArray(adminBDMUsers)
      ? adminBDMUsers.map((user) => ({
          label:
            `${formatWords2(user.firstName ?? "")} ${formatWords2(user.lastName ?? "")}`.trim(),
          id: user.uiid,
        }))
      : [];
    const recruiterOptions = Array.isArray(adminRecruiterUsers)
      ? adminRecruiterUsers.map((user) => ({
          label:
            `${formatWords2(user.firstName ?? "")} ${formatWords2(user.lastName ?? "")}`.trim(),
          id: user.uiid,
        }))
      : [];

    const raIds = raOptions?.map((user) => user.id) || [];
    const bdmIds = bdmOptions?.map((user) => user.id) || [];
    const recruiterIds = recruiterOptions?.map((user) => user.id) || [];

    setSelectedRaUsers((prev) => {
      if (!prev || prev.length === 0) return raIds;
      const prevSet = new Set(prev);
      const validPrev = raIds.filter((id) => prevSet.has(id));
      return validPrev.length ? validPrev : raIds;
    });
    setSelectedBdmUsers((prev) => {
      if (!prev || prev.length === 0) return bdmIds;
      const prevSet = new Set(prev);
      const validPrev = bdmIds.filter((id) => prevSet.has(id));
      return validPrev.length ? validPrev : bdmIds;
    });
    setSelectedRecruiterUsers((prev) => {
      if (!prev || prev.length === 0) return recruiterIds;
      const prevSet = new Set(prev);
      const validPrev = recruiterIds.filter((id) => prevSet.has(id));
      return validPrev.length ? validPrev : recruiterIds;
    });

    // return { raOptions, bdmOptions, recruiterOptions };
  }, [adminRAUsers, adminBDMUsers, adminRecruiterUsers]);

  const metricsOverviewAdv = useMemo(() => {
    const ra = asArray(combineByUserTimezone(adminMetricsOverview?.raMetrics));
    const bdm = asArray(adminMetricsOverview?.bdmMetrics);
    const recruiter = asArray(adminMetricsOverview?.recruiterMetrics);

    return {
      ra: buildTeamMetricsData({
        teamMetrics: ra,
        reportToHierarchy: asArray(adminRAUsers),
        from: raDateRange.from,
        to: raDateRange.to,
        metricConfig: RA_METRIC_CONFIG,
      }),

      bdm: buildTeamMetricsData({
        teamMetrics: bdm,
        reportToHierarchy: asArray(adminBDMUsers),
        from: bdmDateRange.from,
        to: bdmDateRange.to,
        metricConfig: BDM_METRIC_CONFIG,
      }),

      recruiter: buildTeamMetricsData({
        teamMetrics: recruiter,
        reportToHierarchy: asArray(adminRecruiterUsers),
        from: recruiterDateRange.from,
        to: recruiterDateRange.to,
        metricConfig: RECRUITER_METRIC_CONFIG,
      }),
    };
  }, [adminMetricsOverview, raDateRange, bdmDateRange, recruiterDateRange]);

  const metricsGroup = useMemo(() => {
    const ra = asArray(combineByUserTimezone(adminMetricsGroup?.raMetrics));
    const bdm = asArray(adminMetricsGroup?.bdmMetrics);
    const recruiter = asArray(adminMetricsGroup?.recruiterMetrics);

    return {
      raMetrics: buildTeamMetricsData({
        teamMetrics: ra,
        reportToHierarchy: asArray(adminRAUsers),
        from: raColumnDateRange.from,
        to: raColumnDateRange.to,
        metricConfig: RA_METRIC_CONFIG,
      }),

      bdmMetrics: buildTeamMetricsData({
        teamMetrics: bdm,
        reportToHierarchy: asArray(adminBDMUsers),
        from: bdmColumnDateRange.from,
        to: bdmColumnDateRange.to,
        metricConfig: BDM_METRIC_CONFIG,
      }),

      recruiterMetrics: buildTeamMetricsData({
        teamMetrics: recruiter,
        reportToHierarchy: asArray(adminRecruiterUsers),
        from: recruiterColumnDateRange.from,
        to: recruiterColumnDateRange.to,
        metricConfig: RECRUITER_METRIC_CONFIG,
      }),
    };
  }, [
    adminMetricsGroup,
    adminRAUsers,
    adminBDMUsers,
    adminRecruiterUsers,
    raColumnDateRange,
    bdmColumnDateRange,
    recruiterColumnDateRange,
  ]);

  const raColumnDateList = useMemo(
    () => getDateList(raColumnDateRange.from, raColumnDateRange.to),
    [raColumnDateRange],
  );

  const bdmColumnDateList = useMemo(
    () => getDateList(bdmColumnDateRange.from, bdmColumnDateRange.to),
    [bdmColumnDateRange],
  );

  const recruiterColumnDateList = useMemo(
    () =>
      getDateList(recruiterColumnDateRange.from, recruiterColumnDateRange.to),
    [recruiterColumnDateRange],
  );

  const raOverviewColumnDateList = useMemo(
    () => getDateList(raDateRange.from, raDateRange.to),
    [raDateRange],
  );

  const bdmOverviewColumnDateList = useMemo(
    () => getDateList(bdmDateRange.from, bdmDateRange.to),
    [bdmDateRange],
  );

  const recruiterOverviewColumnDateList = useMemo(
    () => getDateList(recruiterDateRange.from, recruiterDateRange.to),
    [recruiterDateRange],
  );

  const handleRaColumnDateChange = useCallback((newRange) => {
    setRaColumnDateRange({
      from: newRange.from ? getStartOfDay(newRange.from) : newRange.from,
      to: newRange.to ? getEndOfDay(newRange.to) : newRange.to,
    });
  }, []);

  const handleBdmColumnDateChange = useCallback((newRange) => {
    setBdmColumnDateRange({
      from: newRange.from ? getStartOfDay(newRange.from) : newRange.from,
      to: newRange.to ? getEndOfDay(newRange.to) : newRange.to,
    });
  }, []);

  const handleRecruiterColumnDateChange = useCallback((newRange) => {
    setRecruiterColumnDateRange({
      from: newRange.from ? getStartOfDay(newRange.from) : newRange.from,
      to: newRange.to ? getEndOfDay(newRange.to) : newRange.to,
    });
  }, []);

  const fetchAllUsers = useCallback(async () => {
    const usersCount = await userRefCount({
      fil: JSON.stringify({ "role.type": { type: "IN", value: [2, 3, 4] } }),
    });

    if (usersCount?.success) {
      dispatch(getAdminUsersStart({ lim: usersCount?.data?.data }));
    }
  }, [dispatch]);

  const handleRaDateChange = useCallback((newRange) => {
    // const newFrom = normalizeDateForComparison(newRange.from);
    // const newTo = normalizeDateForComparison(newRange.to);

    // setRaDateRange((prev) => {
    //   const prevFrom = normalizeDateForComparison(prev.from);
    //   const prevTo = normalizeDateForComparison(prev.to);

    //   if (prevFrom === newFrom && prevTo === newTo) {
    //     return prev;
    //   }
    //   return { from: newRange.from, to: newRange.to };
    // });
    setRaDateRange({
      from: newRange.from ? getStartOfDay(newRange.from) : newRange.from,
      to: newRange.to ? getEndOfDay(newRange.to) : newRange.to,
    });
  }, []);

  const handleBdmDateChange = useCallback((newRange) => {
    // const newFrom = normalizeDateForComparison(newRange.from);
    // const newTo = normalizeDateForComparison(newRange.to);

    // setBdmDateRange((prev) => {
    //   const prevFrom = normalizeDateForComparison(prev.from);
    //   const prevTo = normalizeDateForComparison(prev.to);

    //   if (prevFrom === newFrom && prevTo === newTo) {
    //     return prev;
    //   }
    //   return { from: newRange.from, to: newRange.to };
    // });
    setBdmDateRange({
      from: newRange.from ? getStartOfDay(newRange.from) : newRange.from,
      to: newRange.to ? getEndOfDay(newRange.to) : newRange.to,
    });
  }, []);

  const handleRecruiterDateChange = useCallback((newRange) => {
    // const newFrom = normalizeDateForComparison(newRange.from);
    // const newTo = normalizeDateForComparison(newRange.to);

    // setRecruiterDateRange((prev) => {
    //   const prevFrom = normalizeDateForComparison(prev.from);
    //   const prevTo = normalizeDateForComparison(prev.to);

    //   if (prevFrom === newFrom && prevTo === newTo) {
    //     return prev;
    //   }
    //   return { from: newRange.from, to: newRange.to };
    // });
    setRecruiterDateRange({
      from: newRange.from ? getStartOfDay(newRange.from) : newRange.from,
      to: newRange.to ? getEndOfDay(newRange.to) : newRange.to,
    });
  }, []);

  // Initialize date ranges ONCE when finalDataDuration changes
  // useEffect(() => {
  //   if (finalDataDuration?.from && !initializedRef.current) {
  //     const defaultRange = {
  //       from: finalDataDuration.from,
  //       to: finalDataDuration.to || new Date(),
  //     };
  //     setRaDateRange(defaultRange);
  //     setBdmDateRange(defaultRange);
  //     setRecruiterDateRange(defaultRange);
  //     setRaColumnDateRange(defaultRange);
  //     setBdmColumnDateRange(defaultRange);
  //     setRecruiterColumnDateRange(defaultRange);
  //     initializedRef.current = true;
  //   }
  // }, [finalDataDuration?.from, finalDataDuration?.to]);

  // Fetch all users once on mount
  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  // Fetch initial metrics when finalDataDuration changes
  useEffect(() => {
    if (!finalDataDuration?.from) return;

    // Fetch admin metrics
    const adminFil = {
      addedOn: {
        type: "ADMIN_METRICS",
        startDate: finalDataDuration.from,
        endDate: finalDataDuration.to,
      },
    };
    dispatch(getAdminMetricsStart({ fil: adminFil, isCompare: false }));

    // Fetch compare metrics
    if (compareDateRange) {
      const compareFil = {
        addedOn: {
          type: "ADMIN_METRICS",
          startDate: compareDateRange.from,
          endDate: compareDateRange.to,
        },
      };
      dispatch(
        getAdminCompareMetricsStart({ fil: compareFil, isCompare: true }),
      );
    }
  }, [finalDataDuration, compareDateRange, dispatch]);

  // ========== RA OVERVIEW (Pie Chart) - Only fetches overview ==========
  useEffect(() => {
    if (!raDateRange?.from || !raDateRange?.to) return;

    const overviewFilter = {
      addedOn: {
        type: "RA_METRICS_GROUP",
        value: selectedRaUsers,
        startDate: raDateRange.from,
        endDate: raDateRange.to,
      },
    };

    dispatch(
      getAdminRAMetricsOverviewStart({
        fil: overviewFilter,
        role: "RA",
        type: "overview",
      }),
    );
  }, [raDateRange.from, raDateRange.to, selectedRaUsers, dispatch]);

  // ========== RA GROUP (Column Chart) - Only fetches group ==========
  useEffect(() => {
    if (
      !raColumnDateRange?.from ||
      selectedRaUsers?.length === 0 ||
      !raColumnDateRange?.to
    )
      return;

    const groupFilter = {
      addedOn: {
        type: "RA_METRICS_GROUP",
        value: selectedRaUsers,
        startDate: raColumnDateRange.from,
        endDate: raColumnDateRange.to,
      },
    };
    dispatch(
      getAdminRAMetricsGroupStart({
        fil: groupFilter,
        role: "RA",
        type: "group",
      }),
    );
  }, [raColumnDateRange.from, raColumnDateRange.to, selectedRaUsers, dispatch]);

  // ========== BDM OVERVIEW (Pie Chart) - Only fetches overview ==========
  useEffect(() => {
    if (!bdmDateRange?.from || !bdmDateRange?.to) return;

    const overviewFilter = {
      addedOn: {
        type: "BDM_METRICS_GROUP",
        value: selectedBdmUsers,
        startDate: bdmDateRange.from,
        endDate: bdmDateRange.to,
      },
    };
    dispatch(
      getAdminBDMMetricsOverviewStart({
        fil: overviewFilter,
        role: "BDM",
        type: "overview",
      }),
    );
  }, [bdmDateRange.from, bdmDateRange.to, selectedBdmUsers, dispatch]);

  // ========== BDM GROUP (Column Chart) - Only fetches group ==========
  useEffect(() => {
    if (
      !bdmColumnDateRange?.from ||
      selectedBdmUsers?.length === 0 ||
      !bdmColumnDateRange?.to
    )
      return;

    const groupFilter = {
      addedOn: {
        type: "BDM_METRICS_GROUP",
        value: selectedBdmUsers,
        startDate: bdmColumnDateRange.from,
        endDate: bdmColumnDateRange.to,
      },
    };
    dispatch(
      getAdminBDMMetricsGroupStart({
        fil: groupFilter,
        role: "BDM",
        type: "group",
      }),
    );
  }, [
    bdmColumnDateRange.from,
    bdmColumnDateRange.to,
    selectedBdmUsers,
    dispatch,
  ]);

  // ========== RECRUITER OVERVIEW (Pie Chart) - Only fetches overview ==========
  useEffect(() => {
    if (!recruiterDateRange?.from || !recruiterDateRange?.to) return;

    const overviewFilter = {
      addedOn: {
        type: "RECRUITER_METRICS_GROUP",
        value: selectedRecruiterUsers,
        startDate: recruiterDateRange.from,
        endDate: recruiterDateRange.to,
      },
    };
    dispatch(
      getAdminRecruiterMetricsOverviewStart({
        fil: overviewFilter,
        role: "RECRUITER",
        type: "overview",
      }),
    );
  }, [
    recruiterDateRange.from,
    recruiterDateRange.to,
    selectedRecruiterUsers,
    dispatch,
  ]);

  // ========== RECRUITER GROUP (Column Chart) - Only fetches group ==========
  useEffect(() => {
    if (
      !recruiterColumnDateRange?.from ||
      selectedRecruiterUsers?.length === 0 ||
      !recruiterColumnDateRange?.to
    )
      return;

    const groupFilter = {
      addedOn: {
        type: "RECRUITER_METRICS_GROUP",
        value: selectedRecruiterUsers,
        startDate: recruiterColumnDateRange.from,
        endDate: recruiterColumnDateRange.to,
      },
    };
    dispatch(
      getAdminRecruiterMetricsGroupStart({
        fil: groupFilter,
        role: "RECRUITER",
        type: "group",
      }),
    );
  }, [
    recruiterColumnDateRange.from,
    recruiterColumnDateRange.to,
    selectedRecruiterUsers,
    dispatch,
  ]);
  const rows = [
    {
      id: 1,
      entity: "TEKTREE",
      sNo: 1,
      bdm: "Jospher",
      positiveResponse: 1,
      jobsOpened: 1,
      secondResponse: 1,
      submissionsToday: 1,
      agreementsSentToday: 0,
      agreementsReceived: 0,
      pendingAgreements: 1,
      todaysInterview: 0,
      interviewsTomorrow: 0,
      scheduled: 0,
      yetToSchedule: 1,
      feedbackPending: 1,
      confirmation: 0,
      start: 0,
    },
    {
      id: 2,
      entity: "TEKTREE",
      sNo: 2,
      bdm: "Susan",
      positiveResponse: 0,
      jobsOpened: 0,
      secondResponse: 0,
      submissionsToday: 1,
      agreementsSentToday: 0,
      agreementsReceived: 0,
      pendingAgreements: 1,
      todaysInterview: 0,
      interviewsTomorrow: 0,
      scheduled: 1,
      yetToSchedule: 0,
      feedbackPending: 1,
      confirmation: 0,
      start: 0,
    },
    {
      id: 3,
      entity: "TEKTREE",
      sNo: 3,
      bdm: "Nethan",
      positiveResponse: 2,
      jobsOpened: 1,
      secondResponse: 1,
      submissionsToday: 6,
      agreementsSentToday: 1,
      agreementsReceived: 0,
      pendingAgreements: 1,
      todaysInterview: 0,
      interviewsTomorrow: 0,
      scheduled: 1,
      yetToSchedule: 3,
      feedbackPending: 2,
      confirmation: 0,
      start: 0,
    },
    {
      id: 4,
      entity: "TEKTREE",
      sNo: 4,
      bdm: "Mike",
      positiveResponse: 0,
      jobsOpened: 0,
      secondResponse: 0,
      submissionsToday: 1,
      agreementsSentToday: 1,
      agreementsReceived: 0,
      pendingAgreements: 0,
      todaysInterview: 0,
      interviewsTomorrow: 0,
      scheduled: 1,
      yetToSchedule: 1,
      feedbackPending: 1,
      confirmation: 0,
      start: 0,
    },
    {
      id: 5,
      entity: "TEKTREE",
      sNo: 5,
      bdm: "Ron",
      positiveResponse: 0,
      jobsOpened: 0,
      secondResponse: 0,
      submissionsToday: 0,
      agreementsSentToday: 0,
      agreementsReceived: 1,
      pendingAgreements: 1,
      todaysInterview: 0,
      interviewsTomorrow: 0,
      scheduled: 0,
      yetToSchedule: 1,
      feedbackPending: 1,
      confirmation: 0,
      start: 0,
    },
  ];
  const columns = [
    { key: "sNo", label: "S.No" },
    { key: "bdm", label: "BDM" },
    {
      key: "positiveResponse",
      label: "Positive Response",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "jobsOpened",
      label: "Jobs Opened",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    // {
    //   key: "secondResponse",
    //   label: "Second Response",
    //   total: true,
    //   totalRender: (total) => {
    //     return <div className="text-left">{total}</div>;
    //   },
    // },
    {
      key: "submissionsToday",
      label: "Submissions",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "agreementsSentToday",
      label: "Agreements Sent",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "agreementsReceived",
      label: "Agreements Received",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "pendingAgreements",
      label: "Pending Agreements",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "todaysInterview",
      label: "Today's Interview",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "interviewsTomorrow",
      label: "Interviews for Tomorrow",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "scheduled",
      label: "Scheduled",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "yetToSchedule",
      label: "Yet to Schedule",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "feedbackPending",
      label: "Feedback Pending",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "confirmation",
      label: "Confirmation",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "start",
      label: "Start",
      total: true,
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
  ];

  const mappedRows = mapTableRows(rows, columns);

  const handleKpiClick = (key, label) => {
    setSelectedKpi({ key, label });
    setIsModalOpen(true);
   
  };

  return (
    <div className="flex flex-col gap-7">
      <InfoWrapper>
        <InfoCardNewAdv
          title={"Total Accounts"}
          value={adminMetrics?.totalAccounts || 0}
          backValue={adminCompareMetrics?.totalAccounts || 0}
          icon="apartment"
          onClick={() => handleKpiClick("totalAccounts", "Total Accounts")}
        />
        <InfoCardNewAdv
          // title={"Active Clients"}
          title={"Positives Jobs"}
          value={adminMetrics?.activeClients || 0}
          backValue={adminCompareMetrics?.activeClients || 0}
          icon="apartment"
          onClick={() => handleKpiClick("positivesjobs", "Positives Jobs")}
        />
        <InfoCardNewAdv
          title={"Open Jobs"}
          value={adminMetrics?.openJobs || 0}
          backValue={adminCompareMetrics?.openJobs || 0}
          icon="apartment"
          onClick={() => handleKpiClick("openJobs", "Open Jobs")}
        />
        <InfoCardNewAdv
          title={"Placements"}
          value={adminMetrics?.placements || 0}
          backValue={adminCompareMetrics?.placements || 0}
          icon="apartment"
          onClick={() => handleKpiClick("placements", "Placements")}
        />
        <InfoCardNewAdv
          title={"Backout"}
          value={adminMetrics?.backout || 0}
          backValue={adminCompareMetrics?.backout || 0}
          icon="apartment"
          onClick={() => handleKpiClick("backout", "Backout")}
        />
        <InfoCardNewAdv
          title={"Revenue"}
          value={0}
          backValue={0}
          icon="account_balance_wallet"
          // onClick={() => handleKpiClick("revenue", "Revenue")}
        />
      </InfoWrapper>
       {/* <div className="bg-white rounded-xl shadow-md border border-gray-100 border-b-0 overflow-hidden pb-2">
        <TableAdv
          tableId={`dashboard`}
          rows={mappedRows}
          columns={columns}
          totalCount={rows?.length}
          page={page}
          limit={limit}
          onPaginationChange={setPage}
          onLimitChange={handleLimitChange}
          toolbarRight={{
           
            download: false,
            add: [
              {
                component: (
                  <div className="flex gap-3">
                    <FormFieldSet noTitle type="datepicker"/>
                  </div>
                ),
              },
            ],
          }}
        />
      </div>  */}

      <div className="flex flex-col gap-2">
        <h6>Team Productivity Overview</h6>
        <div className="grid grid-cols-3 lg:grid-cols-2 md:grid-cols-1 xl:grid-cols-3 gap-5">
          <ChartsSetWrapperNew
            title="RA Team Overview"
            type="pie"
            data={metricsOverviewAdv.ra}
            categories={raOverviewColumnDateList}
            options={{
              dateConfig: {
                dateFilter: true,
                dateRange: raDateRange,
                defaultOption: "this week",
                onChange: handleRaDateChange,
              },
              legend: {
                show: false,
              },
              pie: {
                infoSection: {
                  show: true,
                },
              },
            }}
          />

          <ChartsSetWrapperNew
            title="BDM Team Overview"
            type="pie"
            data={metricsOverviewAdv.bdm}
            categories={bdmOverviewColumnDateList}
            options={{
              dateConfig: {
                dateFilter: true,
                dateRange: bdmDateRange,
                defaultOption: "this week",
                onChange: handleBdmDateChange,
              },
              legend: {
                show: false,
              },
              pie: {
                infoSection: {
                  show: true,
                },
              },
            }}
          />

          <ChartsSetWrapperNew
            title="Recruiters Team Overview"
            type="pie"
            data={metricsOverviewAdv.recruiter}
            categories={recruiterOverviewColumnDateList}
            options={{
              dateConfig: {
                dateFilter: true,
                dateRange: recruiterDateRange,
                defaultOption: "this week",
                onChange: handleRecruiterDateChange,
              },
              legend: {
                show: false,
              },
              pie: {
                infoSection: {
                  show: true,
                },
              },
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center mt-5">
          <div>
            <h6>RA Team Overview</h6>
          </div>
        </div>

        <ChartsSetWrapperNew
          title="Team Performance Report"
          type="column"
          data={metricsGroup.raMetrics}
          categories={raColumnDateList}
          options={{
            dateConfig: {
              dateFilter: true,
              dateRange: raColumnDateRange,
              onChange: handleRaColumnDateChange,
              defaultOption: "this week",
            },
            toggleChart: {
              showToggle: true,
            },
            bar: {
              stacked: true,
            },
            seriesFilter: {
              showFilter: true,
              title: "RA Team",
            },
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center mt-5">
          <div>
            <h6>BDM Team Overview</h6>
          </div>
        </div>

        <div className="w-full">
          <ChartsSetWrapperNew
            title="Team Performance Report"
            type="column"
            data={metricsGroup.bdmMetrics}
            categories={bdmColumnDateList}
            options={{
              dateConfig: {
                dateFilter: true,
                dateRange: bdmColumnDateRange,
                onChange: handleBdmColumnDateChange,
                defaultOption: "this week",
              },
              toggleChart: {
                showToggle: true,
              },
              bar: {
                stacked: true,
              },
              seriesFilter: {
                showFilter: true,
                title: "BDM Team",
              },
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center mt-5">
          <div>
            <h6>Recruiter Team Overview</h6>
          </div>
        </div>

        <div className="w-full">
          <ChartsSetWrapperNew
            title="Team Performance Report"
            type="column"
            data={metricsGroup.recruiterMetrics}
            categories={recruiterColumnDateList}
            options={{
              dateConfig: {
                dateFilter: true,
                dateRange: recruiterColumnDateRange,
                onChange: handleRecruiterColumnDateChange,
                defaultOption: "this week",
              },
              toggleChart: {
                showToggle: true,
              },
              bar: {
                stacked: true,
              },
              seriesFilter: {
                showFilter: true,
                title: "Recruiter Team",
              },
            }}
          />
        </div>
      </div>
      {/* <Modal
        isOpen={isModalOpen}
        onClose={() =>
          setIsModalOpen(false) || setSelectedKpi({ key: null, label: null })
        }
        title={selectedKpi.label}
        width="max-w-7xl"
        titleColor={"primary"}
      >
        <KpiDashboardViewModal
          selectedKpi={selectedKpi.key}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedKpi({ key: null, label: null });
          }}
          finalDataDuration={finalDataDuration}
        />
      </Modal> */}
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default AdminDashboard;
